import { useCallback, useMemo } from 'react';
import { type QueryClient, useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';

import {
  getWorkflowRun,
  startWorkflowRun as apiStartWorkflowRun,
  stopWorkflowRun as apiStopWorkflowRun,
} from '../lib/api';
import type { WorkflowRun } from '../types';
import { WorkflowRunStatus, WorkflowId } from '../types';
import { toaster } from '../components/ui/toaster';

export function getWorkflowRunQueryOptions<T extends WorkflowId>(
  projectId: string,
  workflowId: T
): UseQueryOptions<WorkflowRun<T> | null, Error> {
  return {
    queryKey: ['workflow-run', projectId, workflowId],
    queryFn: () => getWorkflowRun(projectId, workflowId),
    enabled: !!projectId && !!workflowId,
    refetchOnWindowFocus: true,
  };
}

export function useWorkflowRun<T extends WorkflowId>(projectId: string, workflowId: T) {
  const queryClient = useQueryClient();

  const queryOptions = getWorkflowRunQueryOptions<T>(projectId, workflowId);
  const { data: workflowRun, isLoading, error } = useQuery<WorkflowRun<T> | null>(queryOptions);

  // Mutation for starting the workflow
  const { mutateAsync: startWorkflow } = useMutation({
    mutationFn: () => apiStartWorkflowRun(projectId, workflowId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: queryOptions.queryKey });

      const previous = queryClient.getQueryData<WorkflowRun>(queryOptions.queryKey);

      if (previous) {
        queryClient.setQueryData(queryOptions.queryKey, {
          ...previous,
          status: WorkflowRunStatus.PENDING,
        });
      }
      return { previous };
    },
    onError: (_err, _, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryOptions.queryKey, context.previous);
      }
      toaster.error({
        title: 'Error re-running validation',
        description: 'Failed to re-run validation. Please try again.',
      });
    },
    onSuccess: () => {
      toaster.success({
        title: 'Validation re-run',
        description: 'The validation has been re-run successfully',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
    },
  });

  // Mutation for stopping the workflow
  const { mutateAsync: stopWorkflow } = useMutation({
    mutationFn: () => apiStopWorkflowRun(projectId, workflowId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: queryOptions.queryKey });

      const previous = queryClient.getQueryData<WorkflowRun>(queryOptions.queryKey);

      if (previous) {
        queryClient.setQueryData(queryOptions.queryKey, {
          ...previous,
          status: WorkflowRunStatus.STOPPED,
        });
      }
      return { previous };
    },
    onError: (_err, _, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryOptions.queryKey, context.previous);
      }
      toaster.error({
        title: 'Error stopping validation',
        description: 'Failed to stop validation. Please try again.',
      });
    },
    onSuccess: () => {
      toaster.success({
        title: 'Validation stopped',
        description: 'The validation has been stopped successfully',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
    },
  });

  const isRunning = useMemo(() => {
    return workflowRun?.status === WorkflowRunStatus.RUNNING || workflowRun?.status === WorkflowRunStatus.PENDING;
  }, [workflowRun]);

  return {
    workflowRun: workflowRun ?? null,
    isLoading,
    error,
    startWorkflow,
    stopWorkflow,
    isRunning,
  };
}

export function useWorkflowRuns(projectId: string) {
  const epdValidation = useWorkflowRun(projectId, WorkflowId.EPD_VALIDATION);
  const epdMatching = useWorkflowRun(projectId, WorkflowId.EPD_MATCHING);

  const startWorkflows = useCallback(async () => {
    await Promise.all([epdValidation.startWorkflow(), epdMatching.startWorkflow()]);
  }, [epdValidation.startWorkflow, epdMatching.startWorkflow]);

  const stopWorkflows = useCallback(async () => {
    await Promise.all([epdValidation.stopWorkflow(), epdMatching.stopWorkflow()]);
  }, [epdValidation.stopWorkflow, epdMatching.stopWorkflow]);

  const areWorkflowsRunning = useMemo(() => {
    return epdValidation.isRunning || epdMatching.isRunning;
  }, [epdValidation.isRunning, epdMatching.isRunning]);

  return {
    validationWorkflowRun: epdValidation.workflowRun,
    matchingWorkflowRun: epdMatching.workflowRun,
    startWorkflows,
    stopWorkflows,
    areWorkflowsRunning,
  };
}

export function handleWorkflowRunUpdated(queryClient: QueryClient, data: WorkflowRun) {
  queryClient.setQueryData(['workflow-run', data.projectId, data.workflowId], data);
}
